<template>
  <module-template
    id="scrolltainer"
    title
    :canEdit="$store.state.bpDefModifyPermission"
    canList
    canDownload
    :isDownloading="exporting"
    :canDelete="$store.state.bpDefDeletePermission"
    :deleteConfirm="false"
    @deleteItem="showConfirm = true"
    @editItem="onEdit"
    @list="listBps"
    @download="onExport"
  >
    <template #title>
      <v-row dense class="px-1" align="center">
        {{ $t("global.business-process.bp-definition") }}
        <v-icon color="white" class="px-3" size="9">pic-arrow-right</v-icon>
        <div>{{ name }}</div>
      </v-row>
    </template>

    <v-row dense class="mb-3">
      <v-col cols="6">
        <basic-card-horizontal
          :headers="cardHeaders"
          :item="cardItems"
          :colsLeft="5"
          :colsRight="7"
        >
          <template #[`item.active`]="{}">
            <v-switch
              v-if="$store.state.bpDefModifyPermission"
              v-model="active"
              dense
              hide-details
              @change="toggleActive"
            >
            </v-switch>
          </template>
        </basic-card-horizontal>
      </v-col>
    </v-row>

    <v-card>
      <v-card-text>
        <ooliba-basic-table
          v-model="selectedVersions"
          :headers="headers"
          :items="items"
          :loading="busy"
          no-pagination
          @row-clicked="onRowClick"
        >
          <template #[`item.createdOn`]="{ item }">
            {{ formatDate(item.createdOn) }}
          </template>

          <template #[`item.createdBy`]="{ item }">
            <span v-if="item.isCreatedByDeleted">
              {{ $t("global.concepts.deleted-user") }}
            </span>

            <span v-else>{{ item.createdBy }}</span>
          </template>
        </ooliba-basic-table>

        <h6 class="py-4">
          {{ details }}
        </h6>

        <v-form
          @submit.prevent="editBpDefinition"
          class="mx-auto"
          v-model="valid"
        >
          <bp-definition-form-fields
            v-if="formData"
            :disabled="!showEdit"
            :isEdit="showEdit"
            :init-values="formData"
            :valid="valid"
            @input="onInput($event)"
            @jsonError="
              showInvalidForm = true;
              formError = $event;
            "
          ></bp-definition-form-fields>

          <form-buttons
            v-if="showEdit"
            :confirmText="$t('global.action.save')"
            :can-submit="valid"
            @cancel="cancelEdit"
          ></form-buttons>
        </v-form>
      </v-card-text>
    </v-card>

    <v-card class="mt-3">
      <v-card-text>
        <BPInstanceImage
          v-if="selectedVersions"
          :flowId="selectedVersions[0]?.flowId"
          :flowUserImageWidth="selectedVersions[0]?.flowUserImageWidth"
          :flowUserImageHeight="selectedVersions[0]?.flowUserImageHeight"
          :flowUserImageZoomable="selectedVersions[0]?.flowUserImageZoomable"
          @click="onTaskClick"
        />

        <v-btn
          v-if="selectedVersions && $store.state.bpDefModifyPermission"
          color="primary"
          class="mb-2"
          @click="onCustomizeImage"
        >
          {{ $t("global.business-process.customize-image") }}
        </v-btn>
      </v-card-text>

      <v-dialog v-model="showConfirm" persistent :width="500">
        <v-overlay absolute :value="deleting">
          <v-chip color="blue" dark>{{ $t("global.msg.deleting") }}</v-chip>
        </v-overlay>

        <v-card>
          <v-card-title>
            <span class="headline">{{ $t("global.action.confirm") }}</span>
          </v-card-title>
          <v-card-text>
            <span>{{ $t("global.business-process.delete-message") }}</span>
          </v-card-text>
          <v-card-actions class="justify-end">
            <v-btn
              depressed
              data-cy-id="confirm-delete-button"
              color="error"
              class="is-action"
              @click="deleteBpDefinition()"
            >
              {{ $t("global.action.yes") }}
            </v-btn>

            <v-btn depressed class="button" @click="showConfirm = false">
              {{ $t("global.action.no") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="showInvalidForm" persistent :width="500">
        <v-card>
          <v-card-title>
            <span class="headline">
              {{ $t("global.business-process.invalid-form") }}
            </span>
          </v-card-title>

          <v-card-text>
            <v-alert
              style="background-color: #fff4e7; border: 1px solid #efebe7"
              icon="pic-warning"
            >
              {{ formError }}
            </v-alert>
            <strong>{{ $t("global.business-process.fix-form") }}</strong>
          </v-card-text>

          <v-card-actions class="justify-end">
            <v-btn
              depressed
              class="button"
              @click="
                showInvalidForm = false;
                formError = '';
              "
            >
              {{ $t("global.action.close") }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </module-template>
</template>

<script>
import ModuleTemplate from "@/components/layout/ModuleTemplate";
import { get, downloadFile, remove, put } from "@/model/api";
import OolibaBasicTable from "@/components/OolibaBasicTable";
import BpDefinitionFormFields from "@/components/businessProcess/bpDefinition/BpDefinitionFormFields";
import FormButtons from "@/components/FormButtons";
import BPInstanceImage from "@/components/businessProcess/BPInstanceImage";
import BasicCardHorizontal from "@/components/BasicCardHorizontal";
import { createInvalidGraphError } from "@/model/util";
import { formatDate } from "@/model/util";

export default {
  name: "BPDefinition",
  components: {
    ModuleTemplate,
    OolibaBasicTable,
    BpDefinitionFormFields,
    FormButtons,
    BPInstanceImage,
    BasicCardHorizontal,
  },

  data() {
    return {
      selectedVersions: undefined,
      bpDefinition: {},
      name: "",
      formData: undefined,
      bpVersions: [],
      headers: [
        { text: this.$t("global.concepts.version"), value: "version" },
        { text: this.$t("global.concepts.created-on"), value: "createdOn" },
        { text: this.$t("global.concepts.created-by"), value: "createdBy" },
      ],
      items: [],
      showEdit: false,
      showConfirm: false,
      deleting: false,
      valid: false,
      busy: true,

      showInvalidForm: false,
      formError: "",

      active: undefined,

      cardHeaders: [
        {
          text: this.$t("global.business-process.number-instances"),
          value: "instances",
        },

        {
          text: this.$t("global.business-process.latest-instance"),
          value: "latestInstance",
        },

        {
          text: this.$t("global.business-process.active"),
          value: "active",
        },
      ],

      cardItems: {
        instances: "",
        latestInstance: "",
      },
      exporting: false,
    };
  },

  computed: {
    details() {
      if (!this.selectedVersions?.length) return "";
      return (
        this.$t("global.concepts.details") +
        " version " +
        this.selectedVersions[0]?.version
      );
    },
  },

  methods: {
    formatDate,

    async toggleActive(newValue) {
      if (newValue) {
        await put(
          "/business-process/" + this.$route.params.bpId + "/unhide"
        ).catch((error) => {
          this.$store.commit("showError", error);
        });
      } else {
        await put(
          "/business-process/" + this.$route.params.bpId + "/hide"
        ).catch((error) => {
          this.$store.commit("showError", error);
        });
      }
    },

    listBps() {
      this.$router.push({ name: "BP definitions" });
    },

    onInput(data) {
      this.formData = data;
    },

    onEdit() {
      this.showEdit = true;
    },

    async onExport() {
      this.exporting = true;
      await downloadFile(
        "/business-process/export/" + this.selectedVersions[0].id
      ).catch((ex) => {
        this.$store.commit("showError", ex);
      });
      this.exporting = false;
    },

    async deleteBpDefinition() {
      this.deleting = true;
      await remove("/business-process/" + this.$route.params.bpId)
        .then(() => {
          this.$router.push({ name: "BP definitions" });
          this.showConfirm = false;
          this.deleting = false;
        })
        .catch((error) => {
          this.$store.commit("showError", error);
          this.showConfirm = false;
          this.deleting = false;
        });
    },

    async editBpDefinition() {
      if (this.formData.form) {
        try {
          JSON.parse(this.formData.form);
        } catch (e) {
          this.showInvalidForm = true;
          this.formError = e.message;
          return;
        }
      }
      await put("/business-process/" + this.$route.params.bpId, this.formData)
        .then(() => {
          this.showEdit = false;
          this.loadData();
        })
        .catch((e) => {
          this.$store.commit("showError", createInvalidGraphError(e));
        });
    },

    async getBpDefinition(id) {
      return await get("/business-process/" + id).catch((error) => {
        this.$store.commit("showError", error);
      });
    },

    async getBpVersions(id) {
      return await get("/business-process/" + id + "/versions").catch(
        (error) => {
          this.$store.commit("showError", error);
        }
      );
    },

    cancelEdit() {
      this.showEdit = false;
      this.loadData();
    },

    onRowClick(clickedRow) {
      this.selectedVersions = [clickedRow];
      this.formData = this.createFormData(
        this.bpDefinition,
        this.selectedVersions[0]
      );
    },

    onCustomizeImage() {
      const bpVersionId = this.selectedVersions[0]?.id;

      if (bpVersionId) {
        this.$router.push({
          name: "Edit BP definition image",
          params: { bpVersionId: bpVersionId },
        });
      }
    },

    createFormData(bpDef, bpDefVersion) {
      const formData = {};

      formData.name = bpDef?.name;
      formData.description = bpDefVersion?.description;
      formData.form = bpDefVersion?.propertyEditorDefinition;
      formData.jsonGraph = bpDefVersion?.flowJsonGraph;

      return formData;
    },

    async getTask(processVersionId, vertexId) {
      return await get(
        "/business-process-version/" +
          processVersionId +
          "/flow-task-config/" +
          vertexId
      ).catch((error) => this.onError(error));
    },

    async onTaskClick(vertexId) {
      if (this.selectedVersions) {
        const task = await this.getTask(this.selectedVersions[0].id, vertexId);

        this.$router.push({
          name: "Process task",
          params: { taskId: task.id },
        });
      }
    },
    async loadData() {
      this.busy = true;
      const bpId = this.$route.params.bpId;

      const instanceStats = await get(
        "/business-process/" + bpId + "/instance-stats"
      ).catch((error) => {
        this.$store.commit("showError", error);
      });
      this.cardItems.instances = instanceStats?.instanceCount;
      this.cardItems.latestInstance = formatDate(instanceStats?.latestCreated);

      this.bpDefinition = await this.getBpDefinition(bpId);
      this.active = !this.bpDefinition?.hidden;
      this.name = this.bpDefinition?.name;
      this.bpVersions = await this.getBpVersions(bpId);

      if (this.bpVersions) {
        this.items = [];
        this.bpVersions.forEach((value) => {
          const item = {};

          item["id"] = value.id;
          item[this.headers[0].value] = value.versionNumber + 1;
          item[this.headers[1].value] = value.dateCreated;
          item[this.headers[2].value] = value.createdBy;
          item.isCreatedByDeleted = value.isCreatedByDeleted;
          item.description = value.description;
          item.propertyEditorDefinition = value.propertyEditorDefinition;
          item.name = value.bpName;
          item.flowId = value.flowId;
          item.flowJsonGraph = value.flowJsonGraph;
          item.flowUserImageWidth = value.flowUserImageWidth;
          item.flowUserImageHeight = value.flowUserImageHeight;
          item.flowUserImageZoomable = value.flowUserImageZoomable;

          this.items.push(item);
        });
        this.selectedVersions = this.items ? [this.items[0]] : undefined;
        if (this.bpDefinition && this.selectedVersions) {
          this.formData = this.createFormData(
            this.bpDefinition,
            this.selectedVersions[0]
          );
        }
      }
      this.busy = false;
    },
  },

  async created() {
    await this.loadData();
  },
};
</script>
